import { useState, useEffect, useCallback } from 'react';
import { img, readdy, portraitAt } from '@/utils/images';
import { fetchCoreManagementMembers, type CoreManagementMember } from '@/utils/adminStore';
import { managementTeam } from '@/mocks/expertsData';
import ScrollReveal from '@/components/base/ScrollReveal';

interface CoreUI {
  name: string;
  title: string;
  message: string;
  years: string;
  imagePrompt: string;
  imageUrl: string;
}

// ====== Cache ======
const CACHE_KEY = 'mgmt_core_team_cache';
const CACHE_TTL = 10 * 60 * 1000;

function getCached(): CoreUI[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL) return null;
    if (!Array.isArray(entry.data) || entry.data.length === 0) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function setCache(data: CoreUI[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch { /* silently ignore */ }
}

function mapMock(): CoreUI[] {
  return managementTeam.map((m) => ({
    name: m.name,
    title: m.title,
    message: m.message || '',
    years: m.years || '',
    imagePrompt: m.imagePrompt || '',
    imageUrl: m.imageUrl || '',
  }));
}

function mapSupabase(data: CoreManagementMember[]): CoreUI[] {
  return data.map((m: CoreManagementMember) => ({
    name: m.name,
    title: m.title,
    message: m.message || '',
    years: m.years || '',
    imagePrompt: m.image_prompt || '',
    imageUrl: m.image_url || '',
  }));
}

export default function ManagementTeam() {
  // Start with cache or mock — content appears instantly
  const [members, setMembers] = useState<CoreUI[]>(() => {
    const cached = getCached();
    if (cached) return cached;
    return mapMock();
  });

  const loadRemote = useCallback(async () => {
    try {
      const data = await fetchCoreManagementMembers();
      if (data && data.length > 0) {
        const ui = mapSupabase(data);
        setMembers(ui);
        setCache(ui);
      }
    } catch {
      // keep current data (mock or cache)
    }
  }, []);

  useEffect(() => {
    loadRemote();
  }, [loadRemote]);

  return (
    <section className="relative bg-[#fafaf7] py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]">
        <img
          src={readdy('management-team-bg-001')}
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal variant="fade-up">
            <div className="text-center mb-14">
              <p className="text-brand-brown text-sm tracking-widest mb-3 font-medium">核心管理团队</p>
              <h2 className="text-3xl md:text-4xl font-bold text-brand-deep leading-tight">
                凝聚行业精英，引领品牌发展
              </h2>
              <p className="text-gray-400 text-sm mt-3 max-w-xl mx-auto">
                麦杰集团核心核心管理团队具备深厚的行业积淀与专业素养，从战略规划到执行落地，为品牌持续健康发展保驾护航
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {members.map((member, i) => (
              <ScrollReveal key={member.name} variant="scale-up" delay={i * 80}>
                <div className="group bg-white rounded-2xl overflow-hidden border border-brand-light/30 hover:border-brand-cream transition-all duration-300 h-full flex flex-col">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={member.imageUrl || portraitAt(i)}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <span className="inline-block text-xs font-medium text-white bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                        {member.title}
                      </span>
                    </div>
                  </div>
                  <div className="py-5 pl-4 pr-5 flex-1">
                    <div className="relative">
                      <i className="ri-double-quotes-l text-brand-light text-lg absolute -top-1 -left-1 opacity-40" />
                      <p className="text-gray-500 text-sm leading-relaxed pl-4 italic">
                        {member.message}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}